/**
 * ControlSchemeCards — the `touchScheme` decision rendered as animated mobile
 * control-style cards (one mini phone-screen SVG per preset, its gesture glyphs
 * looping via the `.scheme-card` CSS animations) instead of plain chips.
 * Everything derives from the `@djgg/controls` catalog: the card list, the
 * per-card gesture drawing (re-densified against the CURRENT param answers,
 * so switching "Left / right only" updates the d-pad card), and the
 * compatibility filter against the creation's `inputScheme` (incompatible
 * presets render disabled with the reason).
 */
import {
  CONTROL_SCHEME_IDS,
  CONTROL_SCHEMES,
  resolveScheme,
  type ControlSchemeId,
  type GestureHint,
  type InputSchemeId,
  type SchemeParams,
} from '@djgg/controls';
import { DECISIONS } from '@djgg/creations';

/** Mini phone-screen canvas (SVG units). */
const W = 128;
const H = 72;

/** The zone rectangle a gesture hint occupies on the mini screen. */
function zoneRect(zone: GestureHint['zone']): { x: number; y: number; w: number; h: number } {
  switch (zone) {
    case 'left':
      return { x: 6, y: 14, w: W / 2 - 10, h: H - 20 };
    case 'right':
      return { x: W / 2 + 4, y: 14, w: W / 2 - 10, h: H - 20 };
    case 'buttons':
      return { x: W - 42, y: H - 34, w: 36, h: 28 };
    case 'full':
      return { x: 6, y: 14, w: W - 12, h: H - 20 };
    default:
      // New profile kinds may introduce a named gutter. It deliberately gets
      // a visible bottom strip in Studio even before it gains custom artwork.
      return { x: 6, y: H - 24, w: W - 12, h: 18 };
  }
}

/** One gesture's glyph, centered in its zone (CSS classes carry the motion). */
function GestureGlyph({ hint }: { hint: GestureHint }) {
  const box = zoneRect(hint.zone);
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  // Cast to string deliberately: the catalog evolves independently from this
  // preview, and unknown gesture kinds retain a safe neutral preview.
  switch (hint.kind as string) {
    case 'stick':
      return (
        <g>
          <circle cx={cx} cy={cy} r={11} className="scheme-card__ring" />
          <circle cx={cx} cy={cy} r={5} className="scheme-card__dot scheme-card__dot--orbit" />
        </g>
      );
    case 'dpad':
      return (
        <g className="scheme-card__pulse">
          <path d={`M ${cx} ${cy - 11} l 5 7 h -10 z`} className="scheme-card__solid" />
          <path d={`M ${cx} ${cy + 11} l 5 -7 h -10 z`} className="scheme-card__solid" />
          <path d={`M ${cx - 11} ${cy} l 7 5 v -10 z`} className="scheme-card__solid" />
          <path d={`M ${cx + 11} ${cy} l -7 5 v -10 z`} className="scheme-card__solid" />
        </g>
      );
    case 'drag-y':
      return (
        <g>
          <line x1={cx} y1={cy - 14} x2={cx} y2={cy + 14} className="scheme-card__line" />
          <circle cx={cx} cy={cy} r={5} className="scheme-card__dot scheme-card__dot--drag-y" />
        </g>
      );
    case 'drag-x':
      return (
        <g>
          <line x1={cx - 16} y1={cy} x2={cx + 16} y2={cy} className="scheme-card__line" />
          <circle cx={cx} cy={cy} r={5} className="scheme-card__dot scheme-card__dot--drag-x" />
        </g>
      );
    case 'drag-both':
      return (
        <g>
          <line x1={cx - 16} y1={cy} x2={cx + 16} y2={cy} className="scheme-card__line" />
          <line x1={cx} y1={cy - 14} x2={cx} y2={cy + 14} className="scheme-card__line" />
          <circle cx={cx} cy={cy} r={5} className="scheme-card__dot scheme-card__dot--drag-both" />
        </g>
      );
    case 'tap':
      return (
        <g>
          <circle cx={cx} cy={cy} r={4} className="scheme-card__solid" />
          <circle cx={cx} cy={cy} r={6} className="scheme-card__ripple" />
        </g>
      );
    case 'hold':
      // A ring CLOSING on the finger, the same charge affordance the overlay
      // draws while a long press runs — and offset, because a hold hint always
      // shares its zone with the tap hint it extends.
      return (
        <g>
          <circle cx={cx} cy={cy + HOLD_GLYPH_DY} r={4} className="scheme-card__solid" />
          <circle cx={cx} cy={cy + HOLD_GLYPH_DY} r={6} className="scheme-card__charge" />
        </g>
      );
    case 'double-tap':
      return (
        <g>
          <circle cx={cx - 5} cy={cy} r={4} className="scheme-card__solid" />
          <circle cx={cx + 5} cy={cy} r={4} className="scheme-card__solid" />
          <circle cx={cx - 5} cy={cy} r={6} className="scheme-card__ripple" />
          <circle cx={cx + 5} cy={cy} r={6} className="scheme-card__ripple" />
        </g>
      );
    case 'tap-target':
      return (
        <g>
          <circle cx={cx} cy={cy} r={4} className="scheme-card__solid" />
          <circle cx={cx} cy={cy} r={11} className="scheme-card__ring" />
        </g>
      );
    case 'tap-sides':
      return (
        <g>
          <line x1={cx - 17} y1={cy} x2={cx - 6} y2={cy} className="scheme-card__line" />
          <line x1={cx + 6} y1={cy} x2={cx + 17} y2={cy} className="scheme-card__line" />
          <circle cx={cx} cy={cy} r={4} className="scheme-card__solid" />
          <circle cx={cx} cy={cy} r={6} className="scheme-card__ripple" />
        </g>
      );
    case 'hold-target':
    case 'relative':
    case 'relative-steer':
      return (
        <g>
          <line x1={cx - 16} y1={cy} x2={cx + 16} y2={cy} className="scheme-card__line" />
          <line x1={cx} y1={cy - 14} x2={cx} y2={cy + 14} className="scheme-card__line" />
          <circle cx={cx} cy={cy} r={5} className="scheme-card__dot scheme-card__dot--drag-both" />
        </g>
      );
    default:
      return null;
  }
}

/** Vertical offset of the hold glyph from its zone centre (SVG units). */
const HOLD_GLYPH_DY = 15;

/** The mini screen for one preset under the CURRENT param answers. */
function SchemePreview({ id, params }: { id: ControlSchemeId; params: SchemeParams }) {
  const resolved = resolveScheme({ scheme: id, params }, null);
  const hints = resolved.def.gestures(resolved.params);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="scheme-card__screen" aria-hidden="true">
      <rect x={1} y={1} width={W - 2} height={H - 2} rx={6} className="scheme-card__frame" />
      {hints.map((hint, i) => {
        const box = zoneRect(hint.zone);
        return (
          <g key={i}>
            <rect
              x={box.x}
              y={box.y}
              width={box.w}
              height={box.h}
              rx={4}
              className="scheme-card__zone"
            />
            <GestureGlyph hint={hint} />
          </g>
        );
      })}
    </svg>
  );
}

export interface ControlSchemeCardsProps {
  /** The stored `touchScheme` answer (undefined reads as `auto`). */
  value: string | undefined;
  /** CURRENT param answers (dpadStyle/dragAxis/stickMode) for live previews. */
  params: SchemeParams;
  /** The creation's effective inputScheme — drives compatibility. */
  inputScheme: InputSchemeId;
  onPick: (id: 'auto' | ControlSchemeId) => void;
  disabled?: boolean;
}

export function ControlSchemeCards({ value, params, inputScheme, onPick, disabled }: ControlSchemeCardsProps) {
  const decision = DECISIONS.touchScheme;
  const auto = decision.options.find((o) => o.id === 'auto');
  const selected = value ?? 'auto';
  return (
    <div className="field" data-testid="decision-touchScheme">
      <label className="field__label">Mobile control style</label>
      {decision.help && (
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
          Choose the default phone control style for this game. Players can use a compatible style that feels
          better to them; keyboard controls are unchanged.
        </p>
      )}
      <div className="scheme-cards">
        <button
          type="button"
          className={`scheme-card ${selected === 'auto' ? 'scheme-card--active' : ''}`}
          onClick={() => onPick('auto')}
          disabled={disabled}
          data-testid="option-touchScheme-auto"
        >
          <svg viewBox={`0 0 ${W} ${H}`} className="scheme-card__screen" aria-hidden="true">
            <rect x={1} y={1} width={W - 2} height={H - 2} rx={6} className="scheme-card__frame" />
            <text x={W / 2} y={H / 2 + 4} textAnchor="middle" className="scheme-card__auto">
              AUTO
            </text>
          </svg>
          <span className="scheme-card__label">{auto?.label ?? 'Automatic'}</span>
          <span className="scheme-card__blurb muted">{auto?.blurb ?? 'Use the game recommendation.'}</span>
        </button>
        {CONTROL_SCHEME_IDS.map((id) => {
          const def = CONTROL_SCHEMES[id];
          const compatible = def.compatibleWith.includes(inputScheme);
          return (
            <button
              type="button"
              key={id}
              className={`scheme-card ${selected === id ? 'scheme-card--active' : ''}`}
              onClick={() => onPick(id)}
              disabled={disabled || !compatible}
              title={compatible ? def.genreHint : `Needs ${def.compatibleWith.join(' or ')} controls`}
              data-testid={`option-touchScheme-${id}`}
              data-control-style={id}
            >
              <SchemePreview id={id} params={params} />
              <span className="scheme-card__label">{def.label}</span>
              <span className="scheme-card__blurb muted">
                {compatible ? def.description : `Needs ${def.compatibleWith.join(' / ')} controls.`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
